// No imports: analytics.ts pulls in @/lib/env, which throws under vitest, so
// the cookie rules live here where a test can reach them.

// A stored click id is worth more than a clean value, so a malformed escape
// hands back the raw text instead of throwing out of every caller.
export const decodeCookieValue = (raw: string): string => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

export const cookieValues = (cookieString: string, name: string): string[] =>
  cookieString
    .split(';')
    .map((pair) => pair.trim())
    .filter((pair) => pair.startsWith(`${name}=`))
    .map((pair) => pair.slice(name.length + 1))
    .filter(Boolean)
    .map(decodeCookieValue);

// fb.<subdomain index>.<creation ms>.<fbclid>; the fbclid is everything after
// the third dot.
const fbclidOf = (fbc: string): string | null => {
  const parts = fbc.split('.');
  return parts.length < 4 ? null : parts.slice(3).join('.');
};

const createdAtOf = (fbc: string): number => Number(fbc.split('.')[2]) || 0;

// Our host-only _fbc and the Meta pixel's parent-domain one can both be
// present, most often on a studio subdomain. The one that matches the click in
// the URL wins, else the newest, never whichever document.cookie lists first.
export const pickFbc = (
  cookieString: string,
  urlFbclid: string | null
): string | undefined => {
  const values = cookieValues(cookieString, '_fbc');
  if (urlFbclid) {
    const match = values.find((value) => fbclidOf(value) === urlFbclid);
    if (match) return match;
  }
  return values.reduce<string | undefined>(
    (newest, value) =>
      newest === undefined || createdAtOf(value) > createdAtOf(newest)
        ? value
        : newest,
    undefined
  );
};

// A new ad click replaces the stored one; the same click keeps its original
// creation time.
export const shouldReplaceFbc = (
  stored: string | undefined,
  urlFbclid: string
): boolean => !stored || fbclidOf(stored) !== urlFbclid;

// Host-only on purpose, no Domain=: every studio on a platform subdomain shares
// the registrable domain, so a domain-wide cookie would hand one studio's
// click id to the next.
export const fbcCookie = (fbclid: string, now: number): string =>
  `_fbc=${encodeURIComponent(`fb.1.${now}.${fbclid}`)}; path=/; max-age=7776000; samesite=lax`;
